'use client';

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useFocusTrap } from 'hooks/dom/useFocusTrap';
import type { AccuracyArea, AccuracyRow, AreaId } from './accuracyLedger';
import { AREA_TONE, SENSOR_DOT, isValidated } from './accuracyTokens';
import { asClause, metricProse, referenceProse } from './accuracySeo';
import styles from './AccuracyStudiesSection.module.css';

/* accuracyTokens' ONGOING pair (#0A5DC2 / #0279E8) is written literally in the
   stylesheet's .flag / .flagDot rules — CSS cannot read the TS constant. */

/* Accuracy studies.
 *
 * An accuracy study sells a number — the figure, what it counts, the
 * instrument it was measured against, how many people it covers — so it is
 * drawn as columns rather than as a card.
 *
 * Two views, not one list: "All" is an index of the nine areas with their
 * tallies, and an area is that area's ledger, grown a few rows at a time. The
 * two controls sit on different rows and different edges on purpose — area
 * chips choose the subject, the status toggle chooses the state of the
 * evidence — because drawn as peers they read as twelve equal filters rather
 * than two dimensions that combine.
 *
 * Every count is derived; none is typed.
 *
 * `areas` is a PROP, never an import: the ledger's 368KB plot JSON must stay
 * on the server, and this is a client component. */

const ROWS = 5;

type Selected = AreaId | 'all';
type Status = 'all' | 'validated' | 'ongoing';

const STATUS_ORDER: Status[] = ['all', 'validated', 'ongoing'];

/* Quoted literals, not `science.accuracy.status.${s}` — a computed key head is
   invisible to the i18n callsite scanner and would be pruned out of the client
   bundle, leaving a raw dotted key on screen. */
const STATUS_I18N_KEY: Record<Status, string> = {
  all: 'science.accuracy.status.all',
  validated: 'science.accuracy.status.validated',
  ongoing: 'science.accuracy.status.ongoing',
};

const matches = (row: AccuracyRow, status: Status) =>
  status === 'all' ||
  (status === 'validated' && isValidated(row)) ||
  (status === 'ongoing' && row.ongoing);

/* An area's studies, flattened out of its buckets, ongoing first — a study
   still gathering people is the live edge of the ledger, not a caveat, and
   only the top of each area's run is guaranteed to be on screen.
 *
 * The sort is per area, so area grouping survives, and it is stable by
 * specification (ES2019), so it promotes the ongoing rows without reshuffling
 * the upstream bucket order within each group. */
const rowsOf = (area: AccuracyArea) =>
  area.buckets
    .flatMap((b) => b.rows)
    .sort((a, b) => Number(b.ongoing) - Number(a.ongoing));

/* Arrow / Home / End across a tablist. Takes everything it needs, so it sits
   at module scope and both the area pills and the status toggle — which now
   renders outside the main component — can reach it. */
const roveTabs = (
  e: React.KeyboardEvent,
  index: number,
  last: number,
  onPick: (i: number) => void,
  refs: React.RefObject<(HTMLButtonElement | null)[]>,
) => {
  let next: number | null = null;
  if (e.key === 'ArrowRight') next = index === last ? 0 : index + 1;
  else if (e.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
  else if (e.key === 'Home') next = 0;
  else if (e.key === 'End') next = last;
  if (next === null) return;
  e.preventDefault();
  onPick(next);
  refs.current[next]?.focus();
};

const AccuracyStudiesSection = ({ areas }: { areas: AccuracyArea[] }) => {
  const t = useTranslations('science');
  const studyTotal = areas.reduce((n, a) => n + a.count, 0);
  const [area, setArea] = useState<Selected>('all');
  const [status, setStatus] = useState<Status>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const frameRef = useRef<HTMLDivElement | null>(null);

  const current =
    area === 'all' ? null : areas.find((a) => a.id === area) ?? null;

  /* Scoped to the chosen area, so the toggle always describes what is in
     front of the reader rather than the library as a whole. */
  const scope = useMemo(
    () => (current ? rowsOf(current) : areas.flatMap(rowsOf)),
    [current, areas],
  );
  const counts = useMemo(
    () => ({
      all: scope.length,
      validated: scope.filter(isValidated).length,
      ongoing: scope.filter((r) => r.ongoing).length,
    }),
    [scope],
  );

  const filtered = useMemo(
    () => (current ? rowsOf(current).filter((r) => matches(r, status)) : []),
    [current, status],
  );
  const total = current ? filtered.length : areas.length;

  /* Whether a "View more" is warranted is a question about the rendered box,
     not about a row count — the frame fits eight rows on a laptop and four on
     a phone, and only the browser knows which. */
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return undefined;
    const check = () => setOverflows(el.scrollHeight - el.clientHeight > 4);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [filtered.length, area, status]);

  const areaChipRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const statusChipRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const pickArea = (next: Selected) => {
    setArea(next);
    setModalOpen(false);
    /* The status control is not on screen in the All index, so a status left
       over from an area would filter the index invisibly. */
    if (next === 'all') setStatus('all');
  };

  /* From 901px the toggle is not in the band — it is in the View all modal,
     beside the close button. Tracked rather than assumed so the same state
     drives both the placement and the guard below. */
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(min-width: 901px)');
    const sync = () => setIsDesktop(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  /* The same guard pickArea makes, for the case where the control lives in a
     modal: on desktop the status is reachable ONLY while that modal is open,
     so a filter chosen inside it would otherwise keep narrowing the band
     behind with nothing on screen to say so or undo it. Closing the modal —
     or crossing to desktop with one already set — clears it. Mobile keeps its
     status, because there the toggle stays in the band where it can be seen
     and changed. */
  useEffect(() => {
    if (isDesktop && !modalOpen) setStatus('all');
  }, [isDesktop, modalOpen]);

  return (
    <section
      className={styles.section}
      id="accuracy-studies"
      aria-label={t('science.accuracy.heading')}
    >
      <div className={styles.inner}>
        <h2 className={styles.heading}>{t('science.accuracy.heading')}</h2>
        {/* No counts in section copy. "55 studies, 41 carrying a published
            figure" was true and derived, but it is the kind of sentence that
            survives the move to a CMS as a frozen string and quietly goes
            wrong the first time someone publishes a study. The tiles below
            already carry live counts, per area. */}
        <p className={styles.sub}>{t('science.accuracy.sub')}</p>

        <div
          className={styles.chips}
          role="tablist"
          aria-label={t('science.accuracy.filterByArea')}
        >
          <button
            ref={(el) => {
              areaChipRefs.current[0] = el;
            }}
            type="button"
            className={styles.chip}
            data-outlined="true"
            data-active={area === 'all' ? 'true' : undefined}
            role="tab"
            aria-selected={area === 'all'}
            tabIndex={area === 'all' ? 0 : -1}
            onClick={() => pickArea('all')}
            onKeyDown={(e) =>
              roveTabs(
                e,
                0,
                areas.length,
                (i) => pickArea(i === 0 ? 'all' : areas[i - 1].id),
                areaChipRefs,
              )
            }
          >
            {t('science.accuracy.allAreas')}
          </button>
          {areas.map((a, i) => (
            <button
              key={a.id}
              ref={(el) => {
                areaChipRefs.current[i + 1] = el;
              }}
              type="button"
              className={styles.chip}
              data-active={area === a.id ? 'true' : undefined}
              role="tab"
              aria-selected={area === a.id}
              tabIndex={area === a.id ? 0 : -1}
              onClick={() => pickArea(a.id)}
              onKeyDown={(e) =>
                roveTabs(
                  e,
                  i + 1,
                  areas.length,
                  (next) => pickArea(next === 0 ? 'all' : areas[next - 1].id),
                  areaChipRefs,
                )
              }
            >
              {a.label} <b>{a.count}</b>
            </button>
          ))}
        </div>

        <div className={styles.contextBar}>
          <div className={styles.barTop}>
            <h3 className={styles.context}>
              {current ? (
                <>
                  <i
                    className={styles.contextDot}
                    style={{ background: AREA_TONE[current.id].fg }}
                  />
                  <span className={styles.contextLabel}>{current.label}</span>
                </>
              ) : (
                <span className={styles.contextLabel}>
                  {t('science.accuracy.allAreas')}
                </span>
              )}
              {/* Phone only. The picker used to sit at the far right of this
                  bar, which meant the chosen area was printed twice on one
                  line. Hanging it off the heading says the same thing once. */}
              <button
                type="button"
                className={styles.pickerChevron}
                onClick={() => setPickerOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={pickerOpen}
                aria-label={t('science.accuracy.chooseArea')}
              >
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 12 12"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M2.5 4.5 6 8l3.5-3.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </h3>
          </div>

          {/* Only inside an area, and only on a phone.
              On the All index the same three numbers are already columns on
              every row, so a filter here would duplicate what is on screen —
              and, worse, quietly re-scope those columns so the index no longer
              described the library.

              From 901px it moves into the View all modal instead: the area
              pills sit directly above this row on desktop, and a second
              segmented control beside them reads as a second filter of the
              same kind. */}
          {current && !isDesktop ? (
            <StatusToggle
              status={status}
              counts={counts}
              refs={statusChipRefs}
              onPick={(next) => {
                setStatus(next);
                setModalOpen(false);
              }}
            />
          ) : null}
        </div>

        {current ? (
          <div className={styles.columnHead} aria-hidden="true">
            <span>{t('science.accuracy.colMeasured')}</span>
            <span>{t('science.accuracy.colSensor')}</span>
            <span>{t('science.accuracy.colAccuracy')}</span>
            <span>{t('science.accuracy.colAgainst')}</span>
            <span>{t('science.accuracy.colSample')}</span>
            <span />
          </div>
        ) : (
          <div className={styles.areaColumnHead} aria-hidden="true">
            <span>{t('science.accuracy.colArea')}</span>
            <span>{t('science.accuracy.colStudies')}</span>
            <span>{t('science.accuracy.status.validated')}</span>
            <span>{t('science.accuracy.comingSoon')}</span>
            <span>{t('science.accuracy.status.ongoing')}</span>
            <span />
          </div>
        )}

        {/* Fixed height, then internal scroll — never a taller page. Letting
            the band size itself meant every chip press jumped the page under
            the reader's cursor. Short areas sit in the reserved space; long
            ones scroll inside it, but only once the reader has asked. Every
            row is in the DOM either way, so nothing is hidden from a
            crawler. */}
        <div
          className={styles.frame}
          ref={frameRef}
          data-cards={current ? undefined : 'true'}
          style={{ '--rows': ROWS } as CSSProperties}
        >
          {current ? (
            <div className={styles.list}>
              {filtered.map((r) => (
                <StudyRow key={r.slug} row={r} />
              ))}
            </div>
          ) : (
            <div className={styles.list} data-cards="true">
              {areas.map((a) => (
                <AreaRow
                  key={a.id}
                  area={a}
                  status={status}
                  onOpen={() => pickArea(a.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* The whole ledger, as links, always in the markup. The band above is
            a picker, so until a reader clicks there is no link to any of the
            fifty-two card pages — invisible to every crawler and AI surface.
         *
         * clip-path, not display:none: it stays in the accessibility tree, so
         * a screen-reader user gets the index rather than being made to
         * operate the picker. Same content either way. */}
        <nav
          className={styles.ledgerIndex}
          aria-label={t('science.accuracy.allStudiesIndex')}
        >
          <ul>
            {areas
              .flatMap(rowsOf)
              .filter((r) => r.status !== 'soon')
              .map((r) => (
                <li key={r.slug}>
                  <a href={`/science/accuracy/${r.slug}/`}>
                    {r.label}
                    {/* C4. The index used to read "Outdoor Walk, 92.4%,
                        measured against ECG chest strap, 4 participants" —
                        nothing in it said what 92.4% COUNTS. The metric has
                        been in the payload all along and only the table cell
                        rendered it, so the one sentence a crawler or a screen
                        reader gets was the one place it was missing. */}
                    {r.figure
                      ? `, ${r.figure}${r.metric ? ` ${metricProse(r.metric)}` : ''}`
                      : ''}
                    {r.reference
                      ? `, measured against ${referenceProse(r.reference)}`
                      : ''}
                    {r.sampleSize ? `, ${asClause(r.sampleSize)}` : ''}
                    {r.ongoing ? t('science.accuracy.ongoingParen') : ''}
                  </a>
                </li>
              ))}
          </ul>
        </nav>

        {/* Always rendered, even with nothing to reveal: a row that appears
            and vanishes with the selection moves everything below the band by
            its own height, which is the reflow the frame exists to stop. */}
        <div className={styles.moreRow}>
          {overflows ? (
            <button
              type="button"
              className={styles.moreBtn}
              onClick={() => setModalOpen(true)}
            >
              {t('science.accuracy.viewAll')} <b>({total})</b>
              <ExpandIcon />
            </button>
          ) : (
            <span className={styles.progress}>
              {t('science.accuracy.allShown', { total })}
            </span>
          )}
        </div>

        {pickerOpen ? (
          <AreaPicker
            areas={areas}
            total={studyTotal}
            active={area}
            onPick={(next) => {
              pickArea(next);
              setPickerOpen(false);
            }}
            onClose={() => setPickerOpen(false)}
          />
        ) : null}

        {modalOpen ? (
          <ListModal
            title={current ? current.label : t('science.accuracy.allAreas')}
            count={total}
            onClose={() => setModalOpen(false)}
            toolbar={
              current && isDesktop ? (
                <StatusToggle
                  inModal
                  status={status}
                  counts={counts}
                  refs={statusChipRefs}
                  /* Stays open, unlike the band's copy: the reader is filtering
                     the list in front of them, and closing it would throw away
                     the result of the tap. */
                  onPick={setStatus}
                />
              ) : null
            }
          >
            {current ? (
              <div className={styles.list}>
                {filtered.map((r) => (
                  <StudyRow key={r.slug} row={r} />
                ))}
              </div>
            ) : (
              <div className={styles.list} data-cards="true">
                {areas.map((a) => (
                  <AreaRow
                    key={a.id}
                    area={a}
                    status={status}
                    onOpen={() => {
                      pickArea(a.id);
                      setModalOpen(false);
                    }}
                  />
                ))}
              </div>
            )}
          </ListModal>
        ) : null}
      </div>
    </section>
  );
};

export default AccuracyStudiesSection;

/* The All / Validated / Ongoing control.
 *
 * One implementation, two homes: in the band on a phone, in the View all
 * modal's header on desktop. Extracted when it moved rather than duplicated,
 * because the roving-tabindex wiring is the part that would drift.
 *
 * `inModal` only picks the wrapper class. The tablist semantics, the counts
 * and the disabled-when-empty rule are the same in both places. */
const StatusToggle = ({
  status,
  counts,
  refs,
  onPick,
  inModal,
}: {
  status: Status;
  counts: Record<Status, number>;
  refs: React.MutableRefObject<(HTMLButtonElement | null)[]>;
  onPick: (next: Status) => void;
  inModal?: boolean;
}) => {
  const t = useTranslations('science');
  return (
    <div
      className={inModal ? styles.panelToggle : styles.toggle}
      role="tablist"
      aria-label={t('science.accuracy.filterByState')}
    >
      {STATUS_ORDER.map((s, i) => (
        <button
          key={s}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          className={styles.seg}
          data-active={status === s ? 'true' : undefined}
          role="tab"
          aria-selected={status === s}
          tabIndex={status === s ? 0 : -1}
          disabled={counts[s] === 0}
          onClick={() => onPick(s)}
          onKeyDown={(e) =>
            roveTabs(e, i, 2, (next) => onPick(STATUS_ORDER[next]), refs)
          }
        >
          {t(STATUS_I18N_KEY[s])} <b>{counts[s]}</b>
        </button>
      ))}
    </div>
  );
};

/* The area picker, as a sheet off the bottom edge. A nine-item menu opening
   downward from the right of a 390px screen has nowhere to go, so the phone
   gets a sheet that rises from the thumb instead of the desktop dropdown. */
const AreaPicker = ({
  areas,
  total,
  active,
  onPick,
  onClose,
}: {
  areas: AccuracyArea[];
  total: number;
  active: Selected;
  onPick: (next: Selected) => void;
  onClose: () => void;
}) => {
  const t = useTranslations('science');
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useFocusTrap(panelRef, {
    onEscape: onClose,
    initialFocus: () => panelRef.current,
  });

  const options: { slug: Selected; label: string; count: number }[] = [
    {
      slug: 'all',
      label: t('science.accuracy.allAreas'),
      count: total,
    },
    ...areas.map((a) => ({
      slug: a.id as Selected,
      label: a.label,
      count: a.count,
    })),
  ];

  return (
    <div
      className={styles.pickerScrim}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={styles.pickerBox}
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('science.accuracy.chooseArea')}
        tabIndex={-1}
      >
        <div className={styles.pickerHead}>
          <p className={styles.pickerTitle}>{t('science.accuracy.areas')}</p>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label={t('science.common.close')}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M1 1l10 10M11 1L1 11"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className={styles.pickerList}>
          {options.map((o) => (
            <button
              key={o.slug}
              type="button"
              className={styles.pickerRow}
              data-active={o.slug === active ? 'true' : undefined}
              aria-current={o.slug === active}
              onClick={() => onPick(o.slug)}
            >
              {o.slug === 'all' ? null : (
                <i
                  className={styles.pickerDot}
                  style={{ background: AREA_TONE[o.slug as AreaId].fg }}
                />
              )}
              <span className={styles.pickerLabel}>{o.label}</span>
              <span className={styles.pickerCount}>{o.count}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* The full list, in a window over the page. The band itself never scrolls — it
   shows exactly what fits, so the page's height is a property of the design —
   and everything beyond that lives here. Body scroll is locked while it is
   open so a dismissal gesture cannot leave the reader elsewhere on the page. */
const ListModal = ({
  title,
  count,
  onClose,
  toolbar,
  children,
}: {
  title: string;
  count: number;
  onClose: () => void;
  /* Sits in the header, between the title and the close button. Desktop puts
     the status toggle here; on a phone it stays in the band and this is
     empty. */
  toolbar?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const t = useTranslations('science');
  const panelRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  /* Pin the body to the height it opens at, so filtering cannot resize it.
   *
   * .panel caps with max-height and otherwise sizes to content, so choosing
   * Validated used to shrink the dialog under the reader's cursor — the thing
   * they were reading jumped, and the toggle they had just used moved with it.
   * The list should change; the frame it sits in should not.
   *
   * Measured once on open rather than computed from a row count: rows are
   * different heights on either side of 900px and a study row is not an area
   * card, so the DOM is the only thing that knows. The modal always opens
   * unfiltered on desktop — closing it clears the status — so the first
   * measurement is the tallest the list will be.
   *
   * clientHeight, not scrollHeight: what is on screen is what must not move.
   * Under the cap the two are the same; past it clientHeight is the capped
   * height and scrollHeight is the whole list, and pinning the latter would
   * grow the dialog rather than hold it. */
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const h = el.clientHeight;
    if (h > 0) el.style.minHeight = `${h}px`;
  }, []);

  useFocusTrap(panelRef, {
    onEscape: onClose,
    initialFocus: () => panelRef.current,
  });

  const countLabel =
    count === 1
      ? t('science.accuracy.studyCountOne')
      : t('science.accuracy.studyCount', { count });

  return (
    <div
      className={styles.scrim}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={styles.panel}
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${title} — ${countLabel}`}
        tabIndex={-1}
      >
        <div className={styles.panelHead}>
          <h3 className={styles.panelTitle}>
            {title}
            <span className={styles.panelCount}>{countLabel}</span>
          </h3>
          {toolbar}
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label={t('science.common.close')}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M1 1l10 10M11 1L1 11"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className={styles.panelBody} ref={bodyRef}>
          {children}
        </div>
      </div>
    </div>
  );
};

/* One area, as a row in the All index. Same spine as a study row, so
   switching between the two views does not reflow the page. */
const AreaRow = ({
  area,
  status,
  onOpen,
}: {
  area: AccuracyArea;
  status: Status;
  onOpen: () => void;
}) => {
  const t = useTranslations('science');
  const rows = rowsOf(area);
  const n = rows.filter((r) => matches(r, status)).length;
  const validated = rows.filter(isValidated).length;
  /* Validated and ongoing are both LIVE states, so on their own they never
     account for the total: Activity read "24 · 10 validated · 11 ongoing" and
     the three studies with no page yet were simply missing from the sum. A
     tally the reader can add up has to name them. */
  const soon = rows.filter((r) => r.status === 'soon').length;
  return (
    <button
      type="button"
      className={styles.areaButton}
      onClick={onOpen}
      disabled={n === 0}
    >
      <span className={styles.cStudy}>
        <i
          className={styles.areaDot}
          style={{ background: AREA_TONE[area.id].fg }}
          aria-hidden="true"
        />
        <span className={styles.areaName}>{area.label}</span>
      </span>
      <span className={styles.aTally}>
        <span className={styles.aCount} data-one={n === 1 ? 'true' : undefined}>
          <span className={styles.figNum}>{n}</span>
        </span>
        <span className={styles.aValidated}>
          {t('science.accuracy.validatedCount', { count: validated })}
        </span>
        <span className={styles.aSoon}>
          {soon > 0 ? t('science.accuracy.soonCount', { count: soon }) : null}
        </span>
        <span className={styles.aOngoing}>
          {area.ongoing > 0
            ? t('science.accuracy.ongoingCount', { count: area.ongoing })
            : null}
        </span>
      </span>
      <span className={styles.cGo} aria-hidden="true">
        <Chevron />
      </span>
    </button>
  );
};

/* A "Coming soon" study has no page to open, so it is not a link and carries
   no chevron. Empty cells stay genuinely empty — never "n/a", never "TBC". */
const StudyRow = ({ row }: { row: AccuracyRow }) => {
  const t = useTranslations('science');
  const cells = (
    <>
      <span className={styles.cStudy}>
        <span className={styles.name}>{row.label}</span>
        {/* Inline, not on a second line: a status that adds a line makes rows
            different heights, and an even rhythm is what lets the eye run down
            the figure column. */}
        {row.ongoing ? (
          <span
            className={styles.flag}
            title={t('science.accuracy.gatheringParticipants')}
          >
            <i className={styles.flagDot} aria-hidden="true" />
            {t('science.accuracy.status.ongoing')}
          </span>
        ) : null}
        {row.status === 'soon' ? (
          <span className={styles.flag} data-muted="true">
            {t('science.accuracy.comingSoon')}
          </span>
        ) : null}
      </span>

      <span className={styles.cSen}>
        <span className={styles.sensors}>
          {row.sensors.map((s) => (
            <i key={s.id} className={styles.sen} title={s.title}>
              <i
                className={styles.senDot}
                style={{ background: SENSOR_DOT[s.id] ?? '#4A4A4A' }}
                aria-hidden="true"
              />
              {s.label}
            </i>
          ))}
        </span>
      </span>

      <span className={styles.cFig}>
        {row.figure ? (
          <>
            <span className={styles.figNum}>{row.figure}</span>
            {/* The metric label stays. Strip it and "14 of 14", "3.7 days" and
                a metabolic 8.7% that is an error rather than a score all
                become unreadable beside a column of percentages. */}
            {row.metric ? (
              <span className={styles.figLabel}>{row.metric}</span>
            ) : null}
          </>
        ) : null}
      </span>

      <span className={styles.cRef}>{row.reference ?? null}</span>
      <span className={styles.cN}>{row.sampleSize ?? null}</span>

      <span className={styles.cGo} aria-hidden="true">
        {row.status === 'soon' ? null : <Chevron />}
      </span>
    </>
  );

  if (row.status === 'soon')
    return <div className={styles.rowStatic}>{cells}</div>;
  return (
    <Link className={styles.rowLink} href={`/science/accuracy/${row.slug}`}>
      {cells}
    </Link>
  );
};

/* Drawn rather than a glyph: a font-substituted chevron shifts weight and
   baseline, and this sits at 1.5px next to 16px type where that shows. */
const Chevron = () => (
  <svg width="7" height="12" viewBox="0 0 7 12" fill="none" aria-hidden="true">
    <path
      d="M1 1l5 5-5 5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* Corners pushing outward — the control opens a modal rather than growing the
   band in place, and a chevron would promise the opposite. */
const ExpandIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M9.5 2H14v4.5M14 2l-4.9 4.9M6.5 14H2V9.5M2 14l4.9-4.9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
